import {
  Box,
  Typography,
  Alert,
  Link,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  Divider,
  Paper,
} from "@mui/material";
import { felixTokens, radius } from "../../../theme";
import { LoadingButton } from "@mui/lab";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Seo from "../../util/seo";
import { signUpWithEmail } from "../../../providers/usuarioProvider";
import { Perfil, TipoDocumento } from "../../../models/enum";
import InputMask from "react-input-mask-next";
import { isEmpty } from "lodash";
import {
  addDoc,
  collection,
  getFirestore,
  onSnapshot,
} from "firebase/firestore";
import { useThemeMode } from "../../../providers/themeModeContext";
import logoAzul from "../../../assets/logo_azul_horizontal.svg";
import logoBranca from "../../../assets/logo_branca_horizontal.svg";

export default function Registro() {
  const [loading, setLoading] = useState(false);
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [escritorio, setEscritorio] = useState("");
  const [documento, setDocumento] = useState("");
  const [senha, setSenha] = useState("");
  const [perfil, setPerfil] = useState<Perfil>(Perfil.CONTADOR);
  const [erros, setErros] = useState({
    escritorio: false,
    documento: false,
    nome: false,
    email: false,
    erroEmail: "",
    senha: false,
    erroSenha: "",
  });
  const [message, setMessage] = useState<{
    text: string;
    type: "success" | "warning" | "error";
  } | null>(null);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const origem = searchParams.get("origem");
  const perfilParam = searchParams.get("perfil");
  const preco = searchParams.get("preco");
  const codigoPromocional = searchParams.get("codigoPromocional");

  const themeMode = useThemeMode();

  useEffect(() => {
    if (perfilParam) {
      setPerfil(perfilParam as Perfil);
    }
  }, [perfilParam]);

  const camposObrigatoriosVazios = () => {
    let camposComErro = {};
    if (!nome) camposComErro = { ...camposComErro, nome: true };
    if (!email) camposComErro = { ...camposComErro, email: true };
    if (!senha)
      camposComErro = {
        ...camposComErro,
        senha: true,
      };
    if (!documento) camposComErro = { ...camposComErro, documento: true };
    if (perfil === Perfil.CONTADOR && !escritorio)
      camposComErro = { ...camposComErro, escritorio: true };
    return camposComErro;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);
    setError("");

    const camposComErro = camposObrigatoriosVazios();

    if (isEmpty(camposComErro)) {
      try {
        const user = await signUpWithEmail(
          email,
          senha,
          {
            origem: origem || "REGISTRAR",
            perfil: perfil,
            nome: nome,
            documento: documento,
            tipoDocumento:
              perfil === Perfil.CONTADOR
                ? TipoDocumento.CNPJ
                : TipoDocumento.CPF,
          },
          perfil === Perfil.CONTADOR
            ? { nome: escritorio, cnpj: documento }
            : undefined
        );

        if (!!preco) {
          const docRef = await addDoc(
            collection(
              getFirestore(),
              `usuarios/${user.uid}/checkout_sessions`
            ),
            {
              price: preco,
              quantity: 1,
              payment_method_types: ["card", "boleto"],
              mode: "subscription",
              billing_address_collection: "required",
              allow_promotion_codes: true,
              success_url: window.location.origin + "/sucesso",
              cancel_url: window.location.href,
              metadata: { origem: origem || "FelixNaBolsa" },
              customer_update: { name: "auto" },
              tax_id_collection: true,
              promotion_code: codigoPromocional || "",
            }
          );

          onSnapshot(docRef, (snap) => {
            const { error, url }: any = snap.data();
            if (error) {
              const errorMessage = `Erro: ${error.message}`;
              setMessage({ text: errorMessage, type: "error" });
              setError(errorMessage);
            }
            if (url) {
              window.location.assign(url);
            }
          });
        } else {
          navigate("/painel");
        }
      } catch (error: any) {
        switch (error.code) {
          case "auth/email-already-in-use":
            const emailError = "Você já possui cadastro, tente fazer login";
            setMessage({
              text: emailError,
              type: "warning",
            });
            setErros((prev) => ({
              ...prev,
              email: true,
              erroEmail: emailError,
            }));
            setError(emailError);
            break;
          case "auth/weak-password":
            const passwordError = "Senha muito fraca, tente outra";
            setMessage({
              text: passwordError,
              type: "warning",
            });
            setErros((prev) => ({
              ...prev,
              senha: true,
              erroSenha: passwordError,
            }));
            setError(passwordError);
            break;
          case "auth/invalid-email":
            const invalidEmailError = "Email inválido";
            setMessage({ text: invalidEmailError, type: "warning" });
            setError(invalidEmailError);
            break;
          default:
            const defaultError = "Erro ao criar conta. Tente novamente";
            setMessage({
              text: defaultError,
              type: "error",
            });
            setError(defaultError);
        }
      } finally {
        setLoading(false);
      }
    } else {
      const camposError = "Preencha todos os campos obrigatórios";
      setMessage({
        text: camposError,
        type: "warning",
      });
      setErros((prev) => ({ ...prev, ...camposComErro }));
      setError(camposError);
      setLoading(false);
    }
  };

  return (
    <main>
      <Seo
        title="Felix na Bolsa"
        titleTemplate="%s · Criar Conta"
        description="Crie sua conta Felix na Bolsa e comece a profissionalizar o controle fiscal para investidores."
      />
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          backgroundColor: "background.default",
        }}
      >
        {/* Seção Esquerda - Editorial: eyebrow + display com palavra em itálico
            accent. Sem gradiente: a personalidade vem da tipografia. */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "center",
            borderRight: 1,
            borderColor: "divider",
            px: { md: 6, lg: 12 },
            py: 8,
          }}
        >
          <Box sx={{ maxWidth: 560 }}>
            <Typography
              variant="eyebrow"
              color="text.secondary"
              sx={{ display: "block", mb: 3 }}
            >
              Felix na Bolsa
            </Typography>
            <Divider
              sx={{
                mb: 4,
                maxWidth: 64,
                borderBottomWidth: 2,
                borderColor: "accentInk",
              }}
            />
            <Typography
              variant="display"
              component="h2"
              sx={{ mb: 4, color: "text.primary" }}
            >
              <Box
                component="span"
                sx={{ fontStyle: "italic", color: "accentInk" }}
              >
                Simplifique
              </Box>{" "}
              seu IR na Bolsa
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontSize: "1.0625rem",
                lineHeight: 1.6,
                maxWidth: 460,
              }}
            >
              Junte-se a milhares de investidores e contadores que já
              automatizaram seus cálculos de impostos
            </Typography>
          </Box>
        </Box>

        {/* Seção Direita - Formulário de Registro */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: { xs: 2, md: 6, lg: 10 },
            py: { xs: 4, md: 6, lg: 8 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 480,
              mx: "auto",
              backgroundColor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: `${radius.xl}px`,
              p: { xs: 3, md: 4 },
            }}
          >
            {/* Logo e Mensagem alinhadas à esquerda */}
            <Box sx={{ mb: 4 }}>
              <Box
                component="img"
                src={themeMode === "light" ? logoAzul : logoBranca}
                alt="Felix na Bolsa"
                sx={{
                  height: 40,
                  mb: 3,
                  display: "block",
                }}
              />
              <Typography
                variant="eyebrow"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Criar conta
              </Typography>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Criar sua conta
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Preencha os dados abaixo para começar
              </Typography>
            </Box>

            <Box>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <FormControl fullWidth>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {message && (
                      <Alert severity={message.type} sx={{ mb: 1 }}>
                        {message.text}
                      </Alert>
                    )}

                    <ToggleButtonGroup
                      color="primary"
                      value={perfil}
                      exclusive
                      fullWidth
                      onChange={(_event, newPerfil) => {
                        if (newPerfil !== null) {
                          setPerfil(newPerfil);
                        }
                      }}
                      aria-label="text alignment"
                      sx={{ "& .MuiToggleButton-root": { py: 1.5 } }}
                    >
                      <ToggleButton value={Perfil.INVESTIDOR}>
                        Investidor
                      </ToggleButton>
                      <ToggleButton value={Perfil.CONTADOR}>
                        Contador / Agente
                      </ToggleButton>
                    </ToggleButtonGroup>

                    {perfil === Perfil.CONTADOR && (
                      <>
                        <TextField
                          variant="outlined"
                          color="primary"
                          fullWidth
                          label="Escritório"
                          type="text"
                          autoComplete="organization"
                          error={erros.escritorio}
                          helperText={
                            erros.escritorio ? "Campo obrigatório" : ""
                          }
                          value={escritorio}
                          onChange={(e) => setEscritorio(e.target.value)}
                        />

                        <InputMask
                          mask={"99.999.999/9999-99"}
                          value={documento}
                          onChange={(event: any) => {
                            setDocumento(event.target.value);
                          }}
                        >
                          <TextField
                            variant="outlined"
                            color="primary"
                            fullWidth
                            label="CNPJ"
                            type="text"
                            error={erros.documento}
                            helperText={
                              erros.documento ? "Campo obrigatório" : ""
                            }
                            sx={documentoInputSx}
                          />
                        </InputMask>
                      </>
                    )}

                    {perfil === Perfil.INVESTIDOR && (
                      <InputMask
                        mask={"999.999.999-99"}
                        value={documento}
                        onChange={(event: any) => {
                          setDocumento(event.target.value);
                        }}
                      >
                        <TextField
                          variant="outlined"
                          color="primary"
                          fullWidth
                          label="CPF"
                          type="text"
                          error={erros.documento}
                          helperText={
                            erros.documento ? "Campo obrigatório" : ""
                          }
                          sx={documentoInputSx}
                        />
                      </InputMask>
                    )}

                    <TextField
                      variant="outlined"
                      color="primary"
                      fullWidth
                      label="Nome"
                      type="text"
                      autoComplete="name"
                      error={erros.nome}
                      helperText={erros.nome ? "Campo obrigatório" : ""}
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                    />

                    <TextField
                      variant="outlined"
                      color="primary"
                      fullWidth
                      label="Email"
                      type="email"
                      autoComplete="email"
                      error={erros.email}
                      helperText={
                        erros.email
                          ? erros.erroEmail || "Campo obrigatório"
                          : ""
                      }
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />

                    <TextField
                      variant="outlined"
                      color="primary"
                      fullWidth
                      name="senha"
                      label="Senha"
                      type="password"
                      id="senha"
                      autoComplete="new-password"
                      error={erros.senha}
                      helperText={
                        erros.senha
                          ? erros.erroSenha || "Campo obrigatório"
                          : ""
                      }
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                    />

                    <LoadingButton
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      type="submit"
                      disabled={
                        !!!nome ||
                        !!!email ||
                        !!!senha ||
                        !!!documento ||
                        (perfil === Perfil.CONTADOR && !!!escritorio)
                      }
                      loading={loading}
                      sx={{ mt: 1, py: 1.5 }}
                    >
                      Criar conta
                    </LoadingButton>

                    <Divider sx={{ my: 1 }} />
                  </Box>
                </FormControl>
              </Box>
            </Box>

            <Box>
              <Box mt={3} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  Já tem uma conta?{" "}
                  <Link
                    href="/login"
                    underline="hover"
                    sx={{ color: "accentInk", fontWeight: 500 }}
                  >
                    Fazer login
                  </Link>
                </Typography>
              </Box>

              <Box mt={3} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  Ao continuar, você concorda com nossos{" "}
                  <Link
                    href="/termos"
                    underline="hover"
                    sx={{ color: "accentInk", fontWeight: 500 }}
                  >
                    Termos de Uso
                  </Link>{" "}
                  e{" "}
                  <Link
                    href="/privacidade"
                    underline="hover"
                    sx={{ color: "accentInk", fontWeight: 500 }}
                  >
                    Política de Privacidade
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </main>
  );
}

/** CPF/CNPJ é dado: mono tabular no campo, para o documento não "dançar"
 *  enquanto a máscara preenche. */
const documentoInputSx = {
  "& .MuiOutlinedInput-input": {
    fontFamily: felixTokens.fontMono,
    fontVariantNumeric: "tabular-nums" as const,
    letterSpacing: "0.02em",
  },
};
